import { useState } from 'react';
import { ChevronLeft, ExternalLink } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useInsumos } from '../api';
import type { Insumo } from '../schemas';
import { CATEGORIA_LABEL, capitaliza, fmtDose, fmtNum, formulacaoLabel } from '../insumos-fmt';
import { NovoInsumoModal } from './NovoInsumoModal';

// Tela 07 · Produto — Ficha (desktop). Detalhe do insumo: composição com
// pH/Kow, bula por cultura, uso na safra (aplicações — módulo futuro),
// modo de ação/rotação, segurança e fonte da bula.
const SEC = 'text-[10.5px] font-semibold uppercase tracking-[0.6px] text-accent';
const CARD =
  'flex w-full flex-col gap-3 rounded-[14px] border border-[var(--agro-card-border)] bg-surface px-4 py-3.5';
const HR = 'h-px w-full bg-black/[0.08] dark:bg-white/[0.08]';
const CHIP_GRUPO =
  'rounded-[5px] bg-black/5 px-[7px] py-[2px] text-[10.5px] font-medium text-text-2 dark:bg-white/[0.08]';

export function FichaInsumoView({ insumoId }: { insumoId: number }) {
  const navigate = useNavigate();
  const { data: insumos, isLoading } = useInsumos();
  const insumo = (insumos ?? []).find((i) => i.id === insumoId);

  if (isLoading) {
    return <p className="px-8 py-10 text-sm text-text-muted">Carregando produto…</p>;
  }
  if (!insumo) {
    return (
      <div className="flex flex-col items-start gap-3 px-8 py-10">
        <p className="text-sm text-text-muted">Produto não encontrado no catálogo.</p>
        <button
          type="button"
          onClick={() => void navigate({ to: '/agronomia/insumos' })}
          className="rounded-full border border-black/[0.15] bg-surface px-4 py-2 text-[13px] font-medium text-text dark:border-white/[0.15]"
        >
          Voltar ao catálogo
        </button>
      </div>
    );
  }
  return <Ficha insumo={insumo} onVoltar={() => void navigate({ to: '/agronomia/insumos' })} />;
}

function Ficha({ insumo: i, onVoltar }: { insumo: Insumo; onVoltar: () => void }) {
  const [editando, setEditando] = useState(false);
  const grupos = [...new Set(i.ativos.map((a) => a.grupo_quimico).filter(Boolean))] as string[];
  const chipsTopo = [
    CATEGORIA_LABEL[i.categoria] ?? capitaliza(i.categoria),
    i.fabricante,
    formulacaoLabel(i.formulacao),
    i.culturas.length ? i.culturas.map(capitaliza).join(' · ') : null,
    i.unidade ? `Unidade: ${i.unidade}` : null,
  ].filter(Boolean) as string[];

  return (
    <div className="flex h-full flex-col gap-[18px] overflow-y-auto bg-bg px-8 pb-7 pt-6">
      {/* header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-[7px]">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onVoltar}
              aria-label="Voltar ao catálogo"
              className="-ml-1.5 flex size-8 items-center justify-center rounded-full text-text-2 hover:bg-surface2 hover:text-text"
            >
              <ChevronLeft size={20} strokeWidth={2} />
            </button>
            <h1 className="text-2xl font-semibold leading-tight text-text">{i.nome_comercial}</h1>
            {i.ativo ? (
              <span className="inline-flex items-center gap-[5px] rounded-full bg-[rgba(50,150,95,0.10)] px-[9px] py-[3px] text-[11.5px] font-medium text-[#32965f]">
                <span className="size-1.5 rounded-full bg-[#32965f]" /> Ativo
              </span>
            ) : (
              <span className="inline-flex items-center gap-[5px] rounded-full bg-surface3 px-[9px] py-[3px] text-[11.5px] font-medium text-text-2">
                <span className="size-1.5 rounded-full bg-text-muted" /> Inativo
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 pl-[26px]">
            {chipsTopo.map((c) => (
              <span key={c} className="rounded-md bg-black/5 px-2 py-[3px] text-[11.5px] font-medium text-text-2 dark:bg-white/[0.08]">
                {c}
              </span>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setEditando(true)}
          className="shrink-0 rounded-full border border-black/[0.15] bg-surface px-[18px] py-[11px] text-[13px] font-medium text-text hover:bg-surface2 dark:border-white/[0.15]"
        >
          Editar produto
        </button>
      </div>

      {editando && <NovoInsumoModal insumo={i} onClose={() => setEditando(false)} />}

      {/* colunas */}
      <div className="flex items-start gap-5">
        {/* esquerda */}
        <div className="flex min-w-0 flex-1 flex-col gap-3.5">
          <div className={CARD}>
            <span className={SEC}>Composição — por princípio ativo</span>
            <div className="grid grid-cols-[minmax(160px,1.4fr)_110px_170px_60px_80px] gap-3">
              {['Princípio ativo', 'Concentração', 'Grupo químico', 'pH', 'Kow (log P)'].map((h) => (
                <span key={h} className="text-[10px] font-semibold uppercase tracking-[0.5px] text-text-muted">
                  {h}
                </span>
              ))}
            </div>
            {i.ativos.map((a, idx) => (
              <div key={idx} className="flex flex-col gap-3">
                <div className={HR} />
                <div className="grid grid-cols-[minmax(160px,1.4fr)_110px_170px_60px_80px] items-center gap-3">
                  <span className="text-[13px] font-medium text-text">{capitaliza(a.nome)}</span>
                  <span className="text-[12.5px] text-text-2">
                    {fmtNum(a.concentracao)} {a.concentracao_unidade ?? ''}
                  </span>
                  <span>
                    {a.grupo_quimico ? <span className={CHIP_GRUPO}>{a.grupo_quimico}</span> : <span className="text-[12.5px] text-text-muted">—</span>}
                  </span>
                  <span className="text-[12.5px] text-text-2">{fmtNum(a.ph)}</span>
                  <span className="text-[12.5px] text-text-2">{fmtNum(a.kow)}</span>
                </div>
              </div>
            ))}
            {i.ativos.length === 0 && <p className="text-[12.5px] text-text-muted">Nenhum princípio ativo cadastrado.</p>}
          </div>

          <div className={CARD}>
            <span className={SEC}>Bula por cultura</span>
            <div className="grid grid-cols-[110px_170px_110px_110px_110px] gap-3">
              {['Cultura', 'Dose', 'Carência', 'Máx. aplic.', 'Intervalo'].map((h) => (
                <span key={h} className="text-[10px] font-semibold uppercase tracking-[0.5px] text-text-muted">
                  {h}
                </span>
              ))}
            </div>
            {i.culturas_bula.map((c) => (
              <div key={c.cultura} className="flex flex-col gap-3">
                <div className={HR} />
                <div className="grid grid-cols-[110px_170px_110px_110px_110px] items-center gap-3">
                  <span>
                    <span className="rounded-full bg-[rgba(233,106,69,0.13)] px-2.5 py-[3px] text-[11.5px] font-medium text-[#c94f2b] dark:text-[#f59e78]">
                      {capitaliza(c.cultura)}
                    </span>
                  </span>
                  <span className="text-[12.5px] text-text-2">{fmtDose(c, true)}</span>
                  <span className="text-[12.5px] text-text-2">{c.carencia_dias != null ? `${c.carencia_dias} dias` : '—'}</span>
                  <span className="text-[12.5px] text-text-2">{c.max_aplicacoes ?? '—'}</span>
                  <span className="text-[12.5px] text-text-2">{c.intervalo_dias != null ? `${c.intervalo_dias} dias` : '—'}</span>
                </div>
              </div>
            ))}
            {i.culturas_bula.length === 0 && <p className="text-[12.5px] text-text-muted">Nenhum limite de bula cadastrado.</p>}
          </div>

          <div className={CARD}>
            <span className={SEC}>Uso na safra</span>
            <p className="text-[12.5px] text-text-muted">
              Nenhuma aplicação registrada com este produto ainda — o histórico aparece aqui quando o
              planejamento de aplicações entrar no ar.
            </p>
          </div>
        </div>

        {/* direita */}
        <div className="flex w-[380px] shrink-0 flex-col gap-3.5">
          <div className={CARD}>
            <span className={SEC}>Modo de ação</span>
            <Linha rotulo="Ação" valor={i.modo_acao ?? '—'} />
            <Linha rotulo="Rotação" valor={`${grupos.length} ${grupos.length === 1 ? 'grupo' : 'grupos'} distintos no mix`} />
            {grupos.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {grupos.map((g) => (
                  <span key={g} className="rounded-full bg-black/5 px-2 py-[3px] text-[10.5px] font-medium text-text-2 dark:bg-white/[0.08]">
                    {g}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className={CARD}>
            <span className={SEC}>Segurança — do produto</span>
            <Linha rotulo="Nº de registro" valor={i.numero_registro ?? '—'} />
            <Linha rotulo="Reentrada" valor={i.reentrada_horas != null ? `${i.reentrada_horas} h` : '—'} />
            <Linha rotulo="Classe toxicológica" valor={i.classe_tox ?? '—'} />
            <Linha rotulo="Classe ambiental" valor={i.classe_ambiental ?? '—'} />
          </div>

          <div className={CARD}>
            <span className={SEC}>Fonte da bula</span>
            {i.fonte_bula_url ? (
              <a
                href={i.fonte_bula_url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 text-xs font-medium text-accent hover:underline"
              >
                <ExternalLink size={13} strokeWidth={2} />
                <span className="truncate">{i.fonte_bula_url.replace(/^https?:\/\/(www\.)?/, '')}</span>
              </a>
            ) : (
              <p className="text-[12.5px] text-text-muted">Sem link de bula cadastrado.</p>
            )}
            <p className="text-[11px] text-text-muted">
              Dose e carência conforme bula vigente — confira a bula antes de recomendar.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-text-muted">{rotulo}</span>
      <span className="text-right text-[12.5px] font-medium text-text">{valor}</span>
    </div>
  );
}
